import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  getTasksQueryKey,
  type TaskItem,
  type TaskStatus,
  updateTask,
} from '../api/tasks.client.js';
import { moveTaskStatus } from '../lib/taskKanban.js';

interface MutationInput {
  taskId: string;
  toStatus: TaskStatus;
}

interface MutationContext {
  previousTasks: TaskItem[] | undefined;
  queryKey: ReturnType<typeof getTasksQueryKey>;
}

export function useTaskStatusMutation() {
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryKey = getTasksQueryKey(workspaceId);

  return useMutation<TaskItem, Error, MutationInput, MutationContext>({
    mutationFn: ({ taskId, toStatus }) => updateTask(taskId, { status: toStatus }),
    onMutate: async ({ taskId, toStatus }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousTasks = queryClient.getQueryData<TaskItem[]>(queryKey);

      if (previousTasks !== undefined) {
        queryClient.setQueryData<TaskItem[]>(
          queryKey,
          moveTaskStatus(previousTasks, taskId, toStatus),
        );
      }

      return { previousTasks, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTasks !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previousTasks);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] });
    },
  });
}
